/* eslint-disable max-lines */
/* eslint-disable import/prefer-default-export */
import { gql } from 'graphql-request';

export const listLineItemsGql = gql`
  query listLineItems(
    $limit: Int = 25
    $offset: Int = 0
    $filter: ListLineItemsFilter
    $sortBy: LineItemsSortBy
  ) {
    listLineItems(limit: $limit, offset: $offset, filter: $filter, sortBy: $sortBy) {
      count
      results: result {
        lineItem {
          requestLineItemId
          taggedForReview
          requestedDate
          needsByDate
          shipByDate
          status
          item {
            ... on TransferRequest {
              patientMedication {
                name
              }
            }
          }
        }
        patient {
          mpi: patientId
          patientDemographics {
            firstName
            lastName
          }
        }
        partner {
          partnerName
        }
      }
    }
  }
`;

export const controlledSubstancesCheckGql = gql`
  mutation controlledSubstancesCheck($checkInput: ControlledSubstanceCheckInput!) {
    controlledSubstancesCheck(input: $checkInput) {
      requestLineItemId
      status
      reviewReason
      taggedForReview
    }
  }
`;

export const reviewLineItemGql = gql`
  mutation reviewLineItem(
    $lineItemId: UUID!
    $reviewResult: Boolean!
    $mpi: UUID!
    $reasonForRejection: String
    $qs1RxNumber: String
  ) {
    reviewLineItem(
      lineItemId: $lineItemId
      reviewResult: $reviewResult
      mpi: $mpi
      reasonForRejection: $reasonForRejection
      qs1RxNumber: $qs1RxNumber
    ) {
      requestLineItemId
      status
      needsByDate
      shipByDate
      requestedDate
      orderId
      item {
        ... on TransferRequest {
          patientMedication {
            name
          }
        }
      }
    }
  }
`;

export const prescriberClarificationCheckGql = gql`
  mutation prescriberClarificationCheck($checkInput: PrescriberClarificationInput!) {
    prescriberClarificationCheck(input: $checkInput) {
      requestLineItemId
      status
      taggedForReview
      reviewReason
    }
  }
`;

export const copayResultCheckGql = gql`
  mutation copayResultCheck($checkInput: CopayResultCheckInput!) {
    copayResultCheck(input: $checkInput) {
      requestLineItemId
      status
      taggedForReview
      reviewReason
    }
  }
`;

export const profitabilityResultCheckGql = gql`
  mutation profitabilityResultCheck($checkInput: ProfitabilityResultCheckInput!) {
    profitabilityResultCheck(input: $checkInput) {
      requestLineItemId
      status
      taggedForReview
      reviewReason
    }
  }
`;

export const getRequestLineItemVerificationDataGql = gql`
  query getRequestLineItemVerificationData($mpi: UUID!, $requestLineItemId: UUID!) {
    getRequestLineItemVerificationData(mpi: $mpi, requestLineItemId: $requestLineItemId) {
      prescriber {
        name
        npi
        dea
        deaState
        phoneNumber
      }
      requestLineItem {
        requestLineItemId
        estimatedFulfillmentDate
        needsByDate
        shipByDate
        qs1RxNumber
        copay
        item {
          ... on TransferRequest {
            patientMedication {
              name
              ndc
              strength
              form
              quantity
              instructions
              dawCode
            }
          }
        }
      }
      allergies {
        allergyType
        description
        patientAllergyId
      }
    }
  }
`;

export const verifyRequestLineItemGql = gql`
  mutation verifyRequestLineItem($input: VerifyRequestLineItemInput!) {
    verifyRequestLineItem(input: $input) {
      requestLineItemId
      status
      taggedForReview
      reviewReason
      needsByDate
      requestedDate
      orderId
      status
      qs1RxNumber
      item {
        ... on TransferRequest {
          patientMedication {
            name
            form
            strength
            quantity
          }
        }
      }
    }
  }
`;

const bulkLabelRowFragment = `
  fragment BulkLabelRow on BulkLabel {
    lineItemId: requestLineItemId
    lineItemStatus
    processingState
    isTaggedForReview
    isMarkedForFillLater
    isOtc
    labelRegenerationStatus
    tags {
      tagName
      expiry
      createdAt
      isSystemTag
    }
    patient {
      mpi: patientId
      patientDemographics {
        firstName
        lastName
      }
    }
    lastPrintedDate
    needsByDate
    shipByDate
    createdAt
    medicationName
    strength
    packSizeValue
    packSizeUnit
    formAlias
    ndc
    productId
    fillType
    orderId
    shippingOption
    inBatchShipment
    isBusinessAddress
    quantity
    partner
    practice
    state
    fulfillingPharmacyId
    source
    isReshipment
    reviewReason
    orderLineItemCount
    orderDate
    lineItemLabelId
    rxNumber
  }
`;

export const listBulkLabelsGql = gql`
  query listBulkLabels(
    $limit: Int = 50
    $offset: Int = 0
    $filter: ListBulkLabelFilter
    $sortBy: BulkLabelSortBy
  ) {
    listBulkLabels(limit: $limit, offset: $offset, filter: $filter, sortBy: $sortBy) {
      count
      results: result {
        ...BulkLabelRow
      }
    }
  }
  ${bulkLabelRowFragment}
`;

export const generateBulkLabelsGql = gql`
  mutation generateBulkLabels(
    $requestLineItemIds: [String!]!
    $filter: ListBulkLabelFilter
    $isBulk: Boolean
  ) {
    generateBulkLabels(requestLineItemIds: $requestLineItemIds, filter: $filter, isBulk: $isBulk)
  }
`;

export const markLineItemsPrintedGql = gql`
  mutation markLineItemsPrinted(
    $lineItemIds: [String!]!
    $markProductsFilled: Boolean
    $isBulkAction: Boolean
  ) {
    markLineItemsPrinted(
      lineItemIds: $lineItemIds
      markProductsFilled: $markProductsFilled
      isBulkAction: $isBulkAction
    ) {
      overallSuccess
      successCount
      failedCount
      results {
        lineItemId
        orderId
        rxNumber
        success
        reason
        labelLotNumbers
        allocatedLotNumbers
        lotDiverged
      }
    }
  }
`;

export const listOrdersForProductFulfillmentGql = gql`
  query listOrdersForProductFulfillment(
    $limit: Int = 25
    $offset: Int = 0
    $filter: OrdersFilter
    $sortBy: OrdersSortBy
  ) {
    listOrdersForProductFulfillment(
      limit: $limit
      offset: $offset
      filter: $filter
      sortBy: $sortBy
    ) {
      count
      orders {
        orderId
        patient {
          mpi: patientId
          patientIdentity {
            qs1PatientId
          }
          patientDemographics {
            firstName
            lastName
          }
        }
        requestId
        requestNeedsByDate
        orderStatus
        partner {
          partnerName
        }
        lineItems {
          itemName
          needsByDate
          shipByDate
          tags {
            tagName
          }
          requestedDate
          itemType
          lineItemId
          quantity
          rxNumber
          lineItemStatus
          form
          strength
          fillType
          shippingOption
          inBatchShipmentQueue
          isBusinessAddress
        }
        partnerAccountId
      }
    }
  }
`;

export const completeEntryGql = gql`
  mutation completeEntry($mpi: UUID!, $lineItemId: UUID!, $state: RequestLineItemStatus!) {
    completeEntry(mpi: $mpi, lineItemId: $lineItemId, state: $state) {
      requestLineItemId
      status
    }
  }
`;

export const assertQs1RxNumberGql = gql`
  mutation assertQs1RxNumber($rxNumber: String!, $mpi: UUID!, $lineItemId: UUID!) {
    assertQs1RxNumber(rxNumber: $rxNumber, mpi: $mpi, lineItemId: $lineItemId) {
      qs1RxNumber
      requestLineItemId
      status
      taggedForReview
      reviewReason
    }
  }
`;

export const updateRequestLineItemStateGql = gql`
  mutation updateRequestLineItemState(
    $mpi: UUID!
    $requestId: UUID!
    $lineItemId: UUID!
    $currentState: RequestLineItemStatus!
    $skipState: Boolean
    $rollbackState: Boolean
  ) {
    updateRequestLineItemState(
      mpi: $mpi
      requestId: $requestId
      lineItemId: $lineItemId
      currentState: $currentState
      skipState: $skipState
      rollbackState: $rollbackState
    ) {
      requestLineItemId
      status
    }
  }
`;

export const bulkFillGql = gql`
  mutation bulkFill($lineItemIds: [String!]!) {
    bulkFill(lineItemIds: $lineItemIds) {
      overallSuccess
      processedCount
      blockedCount
      results {
        lineItemId
        success
        reason
        rxNumber
        orderId
      }
    }
  }
`;

export const listEVisitRequestsGql = gql`
  query listAwaitingEVisitRequests(
    $limit: Int = 25
    $offset: Int = 0
    $filter: AwaitingEVisitRequestFilter
    $sortBy: AwaitingEVisitRequestSortBy
  ) {
    listAwaitingEVisitRequests(limit: $limit, offset: $offset, filter: $filter, sortBy: $sortBy) {
      count
      results: result {
        eVisitRequestId
        status
        partner {
          partnerUuid
          partnerName
        }
        requestedDate
        campaignBundle {
          bundleLabel
          bundleCost
        }
        patient {
          mpi
          firstName
          lastName
        }
      }
    }
  }
`;

export const listRxIntakeRequestsGql = gql`
  query listRxIntakeRequests(
    $limit: Int
    $offset: Int
    $filter: ListRxIntakeRequestsFilter
    $sortBy: ListRxIntakeRequestsSortBy
  ) {
    listRxIntakeRequests(limit: $limit, offset: $offset, filter: $filter, sortBy: $sortBy) {
      count
      result {
        patient {
          firstName
          lastName
        }
        status
        prescriptions {
          description
        }
        practice {
          practiceId
        }
        createdAt
        scriptId
        requestPayload {
          enrichedPrescriptionPayload
          eScriptMetaData
          originalPayload
        }
        partnerAccountId
        partnerId
      }
    }
  }
`;

export const importEScriptRequestsGql = gql`
  mutation importEScriptOrders($input: [ImportEScriptOrderInput!]!) {
    importEScriptOrders(input: $input) {
      eScriptId
      status
      reasons
    }
  }
`;

export const closeEScriptRequestsGql = gql`
  mutation closeEScriptOrders($eScriptIds: [String!]!) {
    closeEScriptOrders(eScriptIds: $eScriptIds)
  }
`;

export const reGenerateLabelGql = gql`
  mutation regenerateLabel($requestLineItemIds: [String!]!) {
    regenerateLabel(requestLineItemIds: $requestLineItemIds)
  }
`;

export const listBulkLabelPrintHistoryGql = gql`
  query listBulkLabelDownloadHistory(
    $limit: Int = 25
    $offset: Int = 0
    $filter: BulkLabelDownloadHistoryFilter
    $sortBy: BulkLabelDownloadHistorySortBy
  ) {
    listBulkLabelDownloadHistory(limit: $limit, offset: $offset, filter: $filter, sortBy: $sortBy) {
      count
      results: result {
        batchPrintHistoryId
        pomUserId
        pomUserName
        createdTime
        fileKey
        signedUrl
        bulkLabelFilter {
          patient
          orderId
          facility
          state
          drugName
          notInPartner
          partner
          practice
          needsByDate
          fillType
          lastPrintedDate
          printed
          filled
          taggedForReview
          reshipments
          batchItems
          orderDate
          labelDate
          createdAt
          createdBetween
          ndcs
        }
      }
    }
  }
`;

// POM-6010: Review Lot & NDC before bulk label print.
/**
 * Whether these line items still have a label being rebuilt.
 *
 * Deliberately tiny and safe to poll — see the schema. It is the socket-free fallback for the
 * regeneration commitBulkLabelLotNdc now queues on our behalf.
 */
export const labelRegenerationStatusesGql = gql`
  query labelRegenerationStatuses($requestLineItemIds: [String!]!) {
    labelRegenerationStatuses(requestLineItemIds: $requestLineItemIds) {
      requestLineItemId
      labelRegenerationStatus
    }
  }
`;

export const bulkLabelLotNdcReviewGql = gql`
  query bulkLabelLotNdcReview($requestLineItemIds: [String!]!, $reviewSessionId: String) {
    bulkLabelLotNdcReview(
      requestLineItemIds: $requestLineItemIds
      reviewSessionId: $reviewSessionId
    ) {
      items {
        requestLineItemId
        orderId
        rxNumber
        productId
        medicationName
        strength
        fillQuantity
        partner
        fulfillingPharmacyId
        fulfillingPharmacyName
        effectiveNdc
        partnerPreferredNdc
        isOtc
        isDispensed
        lotSource
        # POM-6121: may a lot be hand-recorded against THIS row. Core's answer rather than ours --
        # it knows the fill's state and the caller's permissions, and a row core would refuse must
        # not be offered the panel only to be rejected on commit.
        canRecordUnverifiedLot
        recordedLots {
          inventoryLotId
          lotNumber
          ndc
          quantity
          expirationDate
          remainingQty
          # POM-6121: quantity already claimed against this lot but never received. Shown beside
          # the recorded lot so a second hand-recorded claim on top of it is a visible decision.
          unverifiedQty
          # POM-6119: the operator already agreed at commit that this lot would be dispensed short.
          # Without it the grid asks for that decision again on every re-read — and since a commit
          # moves no stock, remainingQty never rises, so the row could never be printed.
          allowShortfallAsUnverified
          # POM-6119: this lot was typed by hand at commit. Such a lot is created at remaining_qty
          # 0 by design, so without this the grid reads it as having nothing usable and tells the
          # operator to record a lot by hand — which is exactly what they just did.
          isManualEntry
          isExpired
          existsInInventory
        }
      }
      availability {
        productId
        fulfillingPharmacyId
        isLockedForPhysicalCount
        groups {
          ndc
          manufacturer
          drugName
          isDefault
          lots {
            inventoryLotId
            lotNumber
            expirationDate
            remainingQty
            daysUntilExpiry
          }
        }
      }
      # POM-6121: what a lot may be hand-recorded against, riding with the review rather than
      # fetched per row. ChangeNdcLotDialog issues no requests of its own by design, so a per-row
      # lookup would have to break that -- and N lookups over one batch can disagree with each
      # other and with the availability snapshot beside them.
      #
      # Keyed by product AND facility, exactly as the availability list is: a lot is stock at
      # one facility, so the same product across two facilities has two different answers.
      recordableLots {
        productId
        fulfillingPharmacyId
        # False when inventory holds no NDC for the product. The panel cannot be used at all
        # then -- there is nothing valid to record against -- and it says so rather than
        # degrading to free text.
        hasInventoryMaster
        # The closed list of NDCs a claim may be recorded against. A claim against an NDC
        # inventory has no master row for cannot be reconciled by anyone downstream.
        ndcOptions {
          ndc
          drugName
          manufacturer
          isDefault
        }
        # Lots that exist but hold no dispensable quantity, so the normal picker hides them.
        # Ordered most-recently-moved first by core; that order is carried, never re-sorted.
        depletedLots {
          inventoryLotId
          lotNumber
          ndc
          expirationDate
          remainingQty
          unverifiedQty
          isExpired
          lastMovementAt
        }
      }
    }
  }
`;

/**
 * POM-6121 — each entry in `changes[].lotAllocations` now carries the whole identity of the lot,
 * not just its id:
 *
 *   { inventoryLotId, lotNumber, expirationDate, quantity, isManualEntry }
 *
 * The NDC stays at the CHANGE level, not per allocation: BulkLabelLotSelectionInput does not
 * declare one, and an unknown input field fails the whole operation rather than being dropped.
 *
 * `inventoryLotId` is NULLABLE. A lot number typed by hand does not exist in inventory yet, so
 * there is no id to send and core resolves-or-creates the row. `isManualEntry` is what says which
 * of the two happened: a hand-recorded claim posts to the unverified bucket even when it names a
 * lot that already exists (the operator claimed a drained row), and that cannot be inferred from a
 * missing id alone.
 *
 * The shape lives in `BulkLabelLotNdcChangeInput` on the wire, so this document declares only the
 * variable type -- which is why the fields are written down here.
 */
export const commitBulkLabelLotNdcGql = gql`
  mutation commitBulkLabelLotNdc(
    $changes: [BulkLabelLotNdcChangeInput!]!
    $reviewSessionId: String
    $batchLineItemIds: [String!]
    $claimShortfallLotIds: [UUID!]
  ) {
    commitBulkLabelLotNdc(
      changes: $changes
      reviewSessionId: $reviewSessionId
      batchLineItemIds: $batchLineItemIds
      claimShortfallLotIds: $claimShortfallLotIds
    ) {
      overallSuccess
      successCount
      failedCount
      results {
        requestLineItemId
        success
        reason
        committedNdc
        committedLotNumbers
        # POM-6535 — set when the batch asks the lot for more than it holds.
        lotNumber
        shortfallQty
      }
      # POM-6536 — the claimed lots that took effect.
      claimedLotIds
      # The commit queues the label regeneration itself — do NOT call regenerateLabel after it.
      # queued: false means the writes landed but the dispatch did not, so those labels still
      # show the previous lot.
      labelRegeneration {
        queued
        requestLineItemIds
      }
    }
  }
`;

export const bulkOverrideLineItemsGql = gql`
  mutation bulkOverrideLineItems($input: BulkLineItemOverrideInput!) {
    bulkOverrideLineItems(input: $input) {
      results {
        lineItemId
        success
      }
      # QUEUED means results is empty and the outcome arrives on the ndc_override_* socket events.
      status
      batchId
      accepted
    }
  }
`;
